import { useMemo, useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";

import {
  exportLeadsCsv,
  listLeads,
  type LeadsFilter,
  type SortKey,
} from "@/api/leads";
import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { Pagination } from "@/components/Pagination";
import { Spinner } from "@/components/Spinner";
import { fmtDate, fmtMoney } from "@/lib/format";

// Values must match the backend SortKey literal exactly — see
// `backend/app/schemas/leads.py::SortKey`.
const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: "created_at_desc", label: "Newest first" },
  { value: "created_at_asc", label: "Oldest first" },
  { value: "amount_desc", label: "Amount (high → low)" },
  { value: "amount_asc", label: "Amount (low → high)" },
  { value: "modified_at_desc", label: "Recently modified" },
];

const PER_PAGE = 25;
const DEFAULT_SORT: SortKey = "created_at_desc";

/**
 * Pull filter state from the URL so the search is shareable / can be
 * deep-linked. Pagination + sort live there too.
 */
function readFilter(params: URLSearchParams): LeadsFilter {
  const num = (k: string): number | undefined => {
    const v = params.get(k);
    if (v === null || v === "") return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };
  const bool = (k: string): boolean | undefined => {
    const v = params.get(k);
    if (v === "true") return true;
    if (v === "false") return false;
    return undefined;
  };

  const filter: LeadsFilter = {
    q: params.get("q") || undefined,
    date_from: params.get("date_from") || undefined,
    date_to: params.get("date_to") || undefined,
    utm_source: params.get("utm_source") || undefined,
    sugar_status: params.get("sugar_status") || undefined,
    client_state: params.get("client_state") || undefined,
    min_amount: num("min_amount"),
    max_amount: num("max_amount"),
    number_verified: bool("number_verified"),
    sort: (params.get("sort") as SortKey) || DEFAULT_SORT,
    page: num("page") ?? 1,
    per_page: PER_PAGE,
  };
  return filter;
}

export function Leads() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = useMemo(() => readFilter(searchParams), [searchParams]);

  // Local form state — committed to URL on submit so the query
  // doesn't refetch on every keystroke.
  const [form, setForm] = useState<LeadsFilter>(filter);
  const [exportError, setExportError] = useState<unknown>(null);
  const [exporting, setExporting] = useState(false);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["leads", filter],
    queryFn: () => listLeads(filter),
    placeholderData: (prev) => prev,
  });

  function commit(next: LeadsFilter) {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(next)) {
      if (v === undefined || v === "" || v === null) continue;
      if (k === "per_page") continue;
      params.set(k, String(v));
    }
    setSearchParams(params);
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    commit({ ...form, page: 1 });
  }

  function reset() {
    const empty: LeadsFilter = { sort: DEFAULT_SORT, page: 1 };
    setForm(empty);
    commit(empty);
  }

  async function onExport() {
    setExportError(null);
    setExporting(true);
    try {
      await exportLeadsCsv(filter);
    } catch (err) {
      setExportError(err);
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Leads"
        subtitle="Search, filter, and export your synced leads"
        actions={
          <button
            type="button"
            onClick={onExport}
            className="btn-secondary"
            disabled={exporting}
          >
            {exporting && <Spinner size="sm" />}
            Export CSV
          </button>
        }
      />

      {/* Filters */}
      <form onSubmit={onSubmit} className="card">
        <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-4">
          <div className="md:col-span-2">
            <label className="label">Search</label>
            <input
              className="input"
              type="search"
              placeholder="Email, phone, name, sugar case id…"
              value={form.q ?? ""}
              onChange={(e) => setForm({ ...form, q: e.target.value })}
            />
          </div>
          <div>
            <label className="label">From</label>
            <input
              className="input"
              type="date"
              value={form.date_from ?? ""}
              onChange={(e) =>
                setForm({ ...form, date_from: e.target.value || undefined })
              }
            />
          </div>
          <div>
            <label className="label">To</label>
            <input
              className="input"
              type="date"
              value={form.date_to ?? ""}
              onChange={(e) =>
                setForm({ ...form, date_to: e.target.value || undefined })
              }
            />
          </div>

          <div>
            <label className="label">UTM source</label>
            <input
              className="input"
              value={form.utm_source ?? ""}
              onChange={(e) =>
                setForm({ ...form, utm_source: e.target.value || undefined })
              }
            />
          </div>
          <div>
            <label className="label">Sugar status</label>
            <input
              className="input"
              value={form.sugar_status ?? ""}
              onChange={(e) =>
                setForm({
                  ...form,
                  sugar_status: e.target.value || undefined,
                })
              }
            />
          </div>
          <div>
            <label className="label">State</label>
            <input
              className="input"
              value={form.client_state ?? ""}
              onChange={(e) =>
                setForm({ ...form, client_state: e.target.value || undefined })
              }
            />
          </div>
          <div>
            <label className="label">Verified phone</label>
            <select
              className="input"
              value={
                form.number_verified === undefined
                  ? ""
                  : form.number_verified
                    ? "true"
                    : "false"
              }
              onChange={(e) => {
                const v = e.target.value;
                setForm({
                  ...form,
                  number_verified:
                    v === "" ? undefined : v === "true",
                });
              }}
            >
              <option value="">Any</option>
              <option value="true">Verified</option>
              <option value="false">Not verified</option>
            </select>
          </div>

          <div>
            <label className="label">Amount min</label>
            <input
              className="input"
              type="number"
              inputMode="numeric"
              value={form.min_amount ?? ""}
              onChange={(e) =>
                setForm({
                  ...form,
                  min_amount:
                    e.target.value === "" ? undefined : Number(e.target.value),
                })
              }
            />
          </div>
          <div>
            <label className="label">Amount max</label>
            <input
              className="input"
              type="number"
              inputMode="numeric"
              value={form.max_amount ?? ""}
              onChange={(e) =>
                setForm({
                  ...form,
                  max_amount:
                    e.target.value === "" ? undefined : Number(e.target.value),
                })
              }
            />
          </div>
          <div>
            <label className="label">Sort by</label>
            <select
              className="input"
              value={form.sort ?? DEFAULT_SORT}
              onChange={(e) =>
                setForm({ ...form, sort: e.target.value as SortKey })
              }
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-4 py-3">
          <button type="button" onClick={reset} className="btn-secondary">
            Reset
          </button>
          <button type="submit" className="btn-primary">
            Apply filters
          </button>
        </div>
      </form>

      {error && <ErrorBanner error={error} />}
      {exportError !== null && (
        <ErrorBanner
          error={exportError}
          onDismiss={() => setExportError(null)}
        />
      )}

      {/* Table */}
      <div className="card">
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Created</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>State</th>
                  <th className="text-right">Amount</th>
                  <th>UTM</th>
                  <th>Sugar status</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td colSpan={8} className="p-6 text-center text-slate-500">
                      <Spinner /> Loading…
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  data?.items.map((lead) => (
                    <tr key={lead.id}>
                      <td className="whitespace-nowrap text-slate-600">
                        {fmtDate(lead.created_at)}
                      </td>
                      <td>
                        <Link
                          to={`/leads/${encodeURIComponent(lead.id)}`}
                          className="font-medium text-brand-700 hover:underline"
                        >
                          {[lead.client_name_first, lead.client_name_last]
                            .filter(Boolean)
                            .join(" ") || (
                            <em className="text-slate-400">unknown</em>
                          )}
                        </Link>
                      </td>
                      <td className="truncate text-slate-700">
                        {lead.client_email ?? "—"}
                      </td>
                      <td className="text-slate-700">
                        {lead.client_phone ?? "—"}
                      </td>
                      <td className="text-slate-700">
                        {lead.client_state ?? "—"}
                      </td>
                      <td className="text-right tabular-nums text-slate-700">
                        {fmtMoney(lead.client_amount)}
                      </td>
                      <td className="text-slate-600">
                        {lead.utm_source ?? "—"}
                      </td>
                      <td className="text-slate-600">
                        {lead.sugar_status ?? "—"}
                      </td>
                    </tr>
                  ))}
                {!isLoading && data?.items.length === 0 && (
                  <tr>
                    <td colSpan={8} className="p-6 text-center text-slate-500">
                      No leads match your filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        {data && (
          <div className="border-t border-slate-200 px-4 py-3">
            <Pagination
              page={data.page}
              pages={data.pages}
              total={data.total}
              onChange={(p) => commit({ ...filter, page: p })}
            />
          </div>
        )}
      </div>

      {isFetching && !isLoading && (
        <div className="text-xs text-slate-400">Refreshing…</div>
      )}
    </div>
  );
}
